import {
  collection,
  addDoc,
  doc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  arrayUnion,
} from 'firebase/firestore';

import {
  COLLECTIONS,
  ContactMessage,
  ContactMessageStatus,
  CreateContactMessageInput,
  TicketReply,
} from '@j-s/dr-shared';

import { getFirebaseFirestore } from '../../firebase/config';
import {
  generateAccessToken,
  generateTicketNumber,
} from '../helpers/ticketTokenHelpers';

import {
  saveTicketToStorage,
  updateSavedTicketStatus,
} from './ticketStorageService';

export interface SendContactMessageResult {
  id: string;
  ticketNumber: string;
  accessToken: string;
}

export async function sendContactMessage(
  input: CreateContactMessageInput
): Promise<SendContactMessageResult> {
  const db = getFirebaseFirestore();
  const colRef = collection(db, COLLECTIONS.MESSAGES);

  const now = Date.now();
  const ticketNumber = generateTicketNumber();
  const accessToken = generateAccessToken();

  const isPhoneCall =
    input.contactMethod === 'phone' &&
    input.phoneResponsePreference === 'call';
  const initialStatus: ContactMessageStatus = isPhoneCall
    ? 'phone_call'
    : 'unread';

  const cleanData: Omit<ContactMessage, 'id'> = {
    name: input.name.trim(),
    contactMethod: input.contactMethod,
    subject: input.subject.trim(),
    message: input.message.trim(),
    ticketNumber,
    accessToken,
    status: initialStatus,
    createdAt: now,
    lastActivityAt: now,
    replies: [],
  };

  if (input.contactMethod === 'email' && input.email) {
    cleanData.email = input.email.trim();
  }

  if (input.contactMethod === 'phone' && input.phone) {
    cleanData.phone = input.phone.trim();
    if (input.phoneResponsePreference) {
      cleanData.phoneResponsePreference = input.phoneResponsePreference;
    }
  }

  if (input.notificationPreferences) {
    cleanData.notificationPreferences = input.notificationPreferences;
  }

  const docRef = await addDoc(colRef, cleanData);

  if (!isPhoneCall) {
    saveTicketToStorage({
      id: docRef.id,
      ticketNumber,
      accessToken,
      subject: input.subject.trim(),
      createdAt: now,
      lastStatus: 'unread',
    });
  }

  return {
    id: docRef.id,
    ticketNumber,
    accessToken,
  };
}

export function subscribeContactMessages(
  callback: (messages: ContactMessage[]) => void
): () => void {
  const db = getFirebaseFirestore();
  const colRef = collection(db, COLLECTIONS.MESSAGES);
  const q = query(colRef, orderBy('createdAt', 'desc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const messages: ContactMessage[] = snapshot.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<ContactMessage, 'id'>),
      }));

      callback(messages);
    },
    (error) => {
      console.warn('Error fetching contact messages from Firestore:', error);
      callback([]);
    }
  );
}

export function subscribeTicket(
  id: string,
  callback: (ticket: ContactMessage | null) => void
): () => void {
  const db = getFirebaseFirestore();
  const docRef = doc(db, COLLECTIONS.MESSAGES, id);

  return onSnapshot(
    docRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);

        return;
      }

      const data = {
        id: snapshot.id,
        ...(snapshot.data() as Omit<ContactMessage, 'id'>),
      };

      updateSavedTicketStatus(id, data.status);
      callback(data);
    },
    (error) => {
      console.warn('Error subscribing to ticket:', error);
      callback(null);
    }
  );
}

export async function addTicketReply(
  id: string,
  reply: Omit<TicketReply, 'id' | 'createdAt'>,
  newStatus?: ContactMessageStatus
): Promise<TicketReply> {
  const db = getFirebaseFirestore();
  const docRef = doc(db, COLLECTIONS.MESSAGES, id);

  const fullReply: TicketReply = {
    id: generateAccessToken().substring(0, 12),
    ...reply,
    createdAt: Date.now(),
  };

  const updates: Record<string, unknown> = {
    replies: arrayUnion(fullReply),
    lastActivityAt: fullReply.createdAt,
  };

  if (newStatus) {
    updates.status = newStatus;
  }

  await updateDoc(docRef, updates);

  if (newStatus) {
    updateSavedTicketStatus(id, newStatus);
  }

  return fullReply;
}

export async function updateContactMessageStatus(
  id: string,
  status: ContactMessageStatus,
  options?: { closedBy?: 'client' | 'admin' }
): Promise<void> {
  const db = getFirebaseFirestore();
  const docRef = doc(db, COLLECTIONS.MESSAGES, id);

  const updates: Record<string, unknown> = {
    status,
    lastActivityAt: Date.now(),
  };

  if (status === 'read') {
    updates.readAt = Date.now();
  }

  if (status === 'closed') {
    updates.closedBy = options?.closedBy ?? 'admin';
    updates.closedAt = Date.now();
  }

  await updateDoc(docRef, updates);
  updateSavedTicketStatus(id, status);
}

export async function deleteContactMessage(id: string): Promise<void> {
  const db = getFirebaseFirestore();
  const docRef = doc(db, COLLECTIONS.MESSAGES, id);

  await updateDoc(docRef, {
    status: 'archived' as ContactMessageStatus,
    archivedAt: Date.now(),
    lastActivityAt: Date.now(),
  });
  updateSavedTicketStatus(id, 'archived');
}

export async function closeTicketByClient(id: string): Promise<void> {
  const db = getFirebaseFirestore();
  const docRef = doc(db, COLLECTIONS.MESSAGES, id);

  await updateDoc(docRef, {
    status: 'closed' as ContactMessageStatus,
    closedBy: 'client',
    closedAt: Date.now(),
    lastActivityAt: Date.now(),
  });
  updateSavedTicketStatus(id, 'closed');
}

export async function reopenTicketByClient(id: string): Promise<void> {
  const db = getFirebaseFirestore();
  const docRef = doc(db, COLLECTIONS.MESSAGES, id);

  await updateDoc(docRef, {
    status: 'unread' as ContactMessageStatus,
    closedBy: null,
    lastActivityAt: Date.now(),
  });
  updateSavedTicketStatus(id, 'unread');
}

export async function markTicketReadByClient(id: string): Promise<void> {
  const db = getFirebaseFirestore();
  const docRef = doc(db, COLLECTIONS.MESSAGES, id);
  const now = Date.now();

  await updateDoc(docRef, {
    status: 'client_read' as ContactMessageStatus,
    clientReadAt: now,
    lastActivityAt: now,
  });
  updateSavedTicketStatus(id, 'client_read');
}

